import * as React from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import axiosInstance from '../utils/axiosInstance';

export function assistantSourceLink(assetId, source) {
  const ref = encodeURIComponent(source.source_ref_id);
  const run = source.metadata?.run_id;
  if (source.source_type === 'asset_run') return `/runs/${ref}`;
  if (source.source_type === 'telemetry') return run ? `/runs/${encodeURIComponent(run)}?tab=telemetry` : `/assets/${assetId}?tab=live`;
  if (source.source_type === 'artifact') return run ? `/runs/${encodeURIComponent(run)}?tab=files` : `/assets/${assetId}?tab=artifacts&source=${ref}`;
  if (source.source_type === 'asset_document') return `/assets/${assetId}?tab=documents&source=${ref}`;
  if (source.source_type === 'asset_state' && source.metadata?.tab === 'commands') return `/assets/${assetId}?tab=commands`;
  return `/assets/${assetId}?tab=overview`;
}

function SourceChips({ assetId, sources }) {
  return <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
    {sources.map((source) => <Chip key={source.source_id || source.metadata?.citation_key} size="small" variant="outlined" clickable
      component={RouterLink} to={assistantSourceLink(assetId, source)}
      label={`${source.metadata?.citation_key ? `[${source.metadata.citation_key}] ` : ''}${source.display_name}${source.metadata?.revision ? ` · Rev ${source.metadata.revision}` : ''}`} />)}
  </Stack>;
}

export default function AssetAssistantPanel({ asset, documents, runs, latestTelemetry, enabled = true }) {
  const [search, setSearch] = useSearchParams();
  const [sessions, setSessions] = React.useState([]);
  const [sessionId, setSessionId] = React.useState(search.get('session') || '');
  const [data, setData] = React.useState(null);
  const [message, setMessage] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const base = `/assets/${asset.asset_id}/assistant/sessions`;
  const active = data?.invocations?.some((invocation) => ['queued', 'running'].includes(invocation.status));
  const liveRun = data?.proposals?.some((proposal) => proposal.run_id && ['pending', 'dispatching', 'queued', 'running'].includes(proposal.run_status));

  React.useEffect(() => {
    let cancelled = false;
    axiosInstance.get(base).then(({ data: result }) => {
      if (cancelled) return;
      setSessions(result.sessions);
      setSessionId((current) => current || result.sessions[0]?.session_id || '');
    }).catch((err) => !cancelled && setError(err.response?.data?.message || 'Unable to load conversations.'));
    return () => { cancelled = true; };
  }, [base]);

  React.useEffect(() => {
    if (!sessionId) { setData(null); return undefined; }
    let cancelled = false;
    let timer;
    const refresh = async () => {
      try {
        const result = await axiosInstance.get(`${base}/${sessionId}`);
        if (!cancelled) { setData(result.data); setError(''); }
        const needsRefresh = result.data.invocations.some((invocation) => ['queued', 'running'].includes(invocation.status))
          || result.data.proposals.some((proposal) => proposal.run_id && ['pending', 'dispatching', 'queued', 'running'].includes(proposal.run_status));
        if (!cancelled && needsRefresh) timer = setTimeout(refresh, 3000);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to refresh EDDIE.');
        if (!cancelled && (active || liveRun)) timer = setTimeout(refresh, 5000);
      }
    };
    refresh();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [base, sessionId, active, liveRun]);

  const chooseSession = (id) => {
    setSessionId(id); setData(null); setError('');
    setSearch((current) => { const next = new URLSearchParams(current); next.set('tab', 'eddie'); next.delete('trace');
      if (id) next.set('session', id); else next.delete('session'); return next; }, { replace: true });
  };

  const send = async (event) => {
    event.preventDefault();
    if (!enabled || !message.trim() || busy || active) return;
    setBusy(true); setError('');
    try {
      let id = sessionId;
      if (!id) {
        const response = await axiosInstance.post(base, {});
        id = response.data.session.session_id;
        setSessions((current) => [response.data.session, ...current]);
        chooseSession(id);
      }
      await axiosInstance.post(`${base}/${id}/messages`, { message: message.trim() });
      setMessage('');
      const response = await axiosInstance.get(`${base}/${id}`);
      setData(response.data);
    } catch (err) { setError(err.response?.data?.message || 'Unable to send your question.'); }
    finally { setBusy(false); }
  };

  const decide = async (proposal, decision) => {
    setBusy(true); setError('');
    try {
      const response = await axiosInstance.post(`${base}/${sessionId}/proposals/${proposal.proposal_id}/decision`, { decision });
      setData(response.data);
    } catch (err) { setError(err.response?.data?.message || 'Unable to process command approval.'); }
    finally { setBusy(false); }
  };

  return <Stack spacing={2}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
      <Box><Typography variant="h5" fontWeight={800}>Ask EDDIE</Typography>
        <Typography color="text.secondary">AI assistance for {asset.asset_name}</Typography></Box>
      <Stack direction="row" spacing={1}>
        {sessions.length > 0 && <TextField select size="small" label="Conversation" value={sessionId} onChange={(event) => chooseSession(event.target.value)} sx={{ minWidth: 190 }} disabled={busy}>
          <MenuItem value="">New conversation</MenuItem>
          {sessions.map((session) => <MenuItem key={session.session_id} value={session.session_id}>{new Date(session.created_at).toLocaleString()}</MenuItem>)}
        </TextField>}
        <Button onClick={() => chooseSession('')} disabled={busy || !enabled}>New conversation</Button>
      </Stack>
    </Stack>
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      <Chip size="small" label={`${documents.filter((document) => document.knowledge_status === 'indexed').length} indexed documents`} />
      <Chip size="small" label={`${runs.length} historical runs`} />
      <Chip size="small" label={latestTelemetry?.configured && Object.keys(latestTelemetry?.fields || {}).length ? 'Live data available' : 'Live data unavailable'} />
    </Stack>
    {error && <Alert severity="error">{error}</Alert>}
    {!enabled && <Alert severity="info">EDDIE is disabled for this asset. Existing conversations remain available.</Alert>}
    {!data?.messages?.length && <Paper variant="outlined" sx={{ p: 3 }}><Typography>Ask about this asset’s documents, recent runs, telemetry, or available commands.</Typography></Paper>}
    <Stack spacing={2} aria-live="polite">
      {(data?.invocations || []).map((invocation) => {
        const messages = data.messages.filter((item) => item.assistant_invocation_id === invocation.assistant_invocation_id);
        const sources = messages.flatMap((item) => item.sources || []);
        const events = data.events.filter((item) => item.trace_id === invocation.trace_id);
        const proposals = data.proposals.filter((item) => item.trace_id === invocation.trace_id);
        return <Paper key={invocation.trace_id} id={`trace-${invocation.trace_id}`} variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            {messages.map((item) => <Box key={item.assistant_message_id}>
              <Typography variant="subtitle2" fontWeight={800}>{item.role === 'user' ? 'You' : 'EDDIE'}</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.content}</Typography>
              {item.sources?.length > 0 && <Box sx={{ mt: 1 }}><Typography variant="caption">Sources</Typography><SourceChips assetId={asset.asset_id} sources={item.sources} /></Box>}
            </Box>)}
            {['queued', 'running'].includes(invocation.status) && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography>EDDIE is gathering information…</Typography></Stack>}
            {invocation.status === 'failed' && <Alert severity="error">{invocation.error}</Alert>}
            {proposals.map((proposal) => <Paper key={proposal.proposal_id} variant="outlined" sx={{ p: 2, borderColor: 'primary.main' }}>
              <Typography variant="overline">EDDIE recommends</Typography><Typography variant="h6">{proposal.display_name}</Typography>
              {Object.entries(proposal.parameters).map(([key, value]) => <Typography key={key} variant="body2">{key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}</Typography>)}
              <Typography sx={{ my: 1 }}>{proposal.explanation}</Typography>
              <SourceChips assetId={asset.asset_id} sources={sources.filter((source) => proposal.source_keys.includes(source.metadata?.citation_key))} />
              {proposal.status === 'pending' && <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button variant="contained" onClick={() => decide(proposal, 'approve')} disabled={!enabled || busy || invocation.status !== 'completed'}>Execute Command</Button>
                <Button onClick={() => decide(proposal, 'reject')} disabled={busy}>Cancel</Button>
              </Stack>}
              {proposal.status === 'rejected' && <Typography color="text.secondary" sx={{ mt: 1 }}>Cancelled</Typography>}
              {proposal.run_id && <Stack spacing={1} sx={{ mt: 2 }}>
                <Typography fontWeight={700}>Run {proposal.run_id.slice(0, 8)}</Typography>
                <Typography>{proposal.run_status}{proposal.run_progress != null ? ` · ${proposal.run_progress}%` : ''}</Typography>
                <Button component={RouterLink} to={`/runs/${proposal.run_id}`}>View Run</Button>
              </Stack>}
            </Paper>)}
            <Accordion defaultExpanded={search.get('trace') === invocation.trace_id} disableGutters elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2">How EDDIE answered</Typography></AccordionSummary>
              <AccordionDetails><Stack spacing={1}>
                <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>Trace: {invocation.trace_id}</Typography>
                {events.map((entry) => <Box key={entry.event_id}>
                  <Typography variant="body2">{entry.status === 'failed' ? '✕' : entry.status === 'started' ? '…' : '✓'} {entry.summary}{entry.duration_ms != null ? ` (${entry.duration_ms} ms)` : ''}</Typography>
                  {Object.keys(entry.data || {}).length > 0 && <Box component="details"><Typography component="summary" variant="caption">Details</Typography>
                    <Box component="pre" sx={{ fontSize: 12, overflow: 'auto', maxHeight: 240, whiteSpace: 'pre-wrap' }}>{JSON.stringify(entry.data, null, 2)}</Box></Box>}
                </Box>)}
              </Stack></AccordionDetails>
            </Accordion>
          </Stack>
        </Paper>;
      })}
    </Stack>
    <Stack component="form" onSubmit={send} spacing={1}>
      <TextField multiline minRows={2} maxRows={8} label="Ask EDDIE" value={message} onChange={(event) => setMessage(event.target.value)} inputProps={{ maxLength: 8000 }} disabled={!enabled || busy || active} />
      <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="caption" color="text.secondary">Commands require your approval before execution.</Typography>
        <Button type="submit" variant="contained" disabled={!enabled || !message.trim() || busy || active}>Send</Button></Stack>
    </Stack>
    {liveRun && <Typography variant="caption" color="text.secondary">Run status updates automatically.</Typography>}
  </Stack>;
}
